<template>
  <v-container>
    <v-card outlined class="pa-4 pa-md-6">
      <div class="text-h5 mb-6 font-weight-bold">
        {{ $vuetify.lang.t('$vuetify.gpt_broadcast.title') }}
        <v-tooltip bottom max-width="400">
          <template v-slot:activator="{ on, attrs }">
            <span class="info-icon" v-bind="attrs" v-on="on" style="margin-left: 12px; vertical-align: middle;">i</span>
          </template>
          <span>{{ $vuetify.lang.t('$vuetify.gpt_broadcast.title_tooltip') }}</span>
        </v-tooltip>
      </div>

      <!-- New Broadcast Message Section -->
      <v-card class="mb-8 pa-4" outlined>
        <div class="text-h6 mb-4">{{ $vuetify.lang.t('$vuetify.gpt_broadcast.create_new') }}</div>
        <v-form ref="createBroadcastForm">
        <v-row>
          <v-col cols="12">
            <div class="textarea-with-tooltip">
              <v-textarea
                v-model="newBroadcast.message"
                :label="$vuetify.lang.t('$vuetify.gpt_broadcast.message')"
                :rules="[requiredMessageRule]"
                outlined
                rows="3"
                hide-details="auto"
              ></v-textarea>
              <v-tooltip bottom max-width="400">
                <template v-slot:activator="{ on, attrs }">
                  <span class="info-icon" v-bind="attrs" v-on="on">i</span>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.gpt_broadcast.message_tooltip') }}</span>
              </v-tooltip>
            </div>
          </v-col>

          <v-col cols="12">
            <div class="profile-autocomplete-wrapper">
              <v-autocomplete
                class="profile-autocomplete-chips"
                v-model="newBroadcast.profile_ids"
                :items="gptProfilesLocal"
                item-text="name"
                item-value="id"
                :label="$vuetify.lang.t('$vuetify.gpt_broadcast.select_profile')"
                outlined
                dense
                multiple
                chips
                small-chips
                deletable-chips
                clearable
                :menu-props="{ closeOnClick: true, closeOnContentClick: false }"
              >
              <template
                v-slot:append-item
                v-if="gptProfilesLocal.length < gpt_profiles_total_hits"
              >
                <v-list-item>
                  <v-list-item-content>
                    <div class="d-flex">
                      <v-btn
                        class="ma-0 pa-2"
                        @click="loadMoreProfiles()"
                        color="primary"
                        text
                        :loading="isLoadingProfiles"
                      >
                        {{ $vuetify.lang.t("$vuetify.common.load_more") }}
                      </v-btn>
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-autocomplete>
            <v-tooltip bottom max-width="350">
              <template v-slot:activator="{ on, attrs }">
                <span
                  class="info-icon profile-tooltip-icon"
                  v-bind="attrs"
                  v-on="on"
                  @mousedown.prevent.stop
                  @click.stop
                  >i</span>
              </template>
                <span>{{ $vuetify.lang.t('$vuetify.gpt_broadcast.profile_tooltip') }}</span>
            </v-tooltip>
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="delivery-mode-wrapper">
              <v-select
                v-model="newBroadcast.delivery_mode"
                :items="deliveryModeOptions"
                :label="$vuetify.lang.t('$vuetify.gpt_broadcast.delivery_mode')"
                outlined
                dense
              ></v-select>
              <v-tooltip bottom max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <span
                    class="info-icon delivery-mode-tooltip-icon"
                    v-bind="attrs"
                    v-on="on"
                    @mousedown.prevent.stop
                    @click.stop
                  >i</span>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.gpt_broadcast.delivery_mode_tooltip') }}</span>
              </v-tooltip>
            </div>
          </v-col>
          
          <v-col cols="12" md="6" v-if="newBroadcast.delivery_mode === 'scheduled'">
            <div style="position: relative;">
              <v-datetime-picker
                v-model="newBroadcast.delivery_time"
                :text-field-props="{
                  outlined: true,
                  dense: true,
                  label: $vuetify.lang.t('$vuetify.gpt_broadcast.delivery_time'),
                  placeholder: $vuetify.lang.t('$vuetify.gpt_broadcast.delivery_time_placeholder'),
                  rules: [requiredDeliveryTimeRule]
                }"
                :date-picker-props="{ min: new Date().toISOString().split('T')[0] }"
                :time-picker-props="{ format: '24hr' }"
              >
              </v-datetime-picker>
              <v-tooltip bottom max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <span v-on="on" v-bind="attrs" class="info-icon info-icon-scheduled">i</span>
                </template>
                <span>{{ $vuetify.lang.t('$vuetify.gpt_broadcast.delivery_time_tooltip') }}</span>
              </v-tooltip>
            </div>
          </v-col>
        </v-row>
        <v-row class="mt-4">
          <v-col class="text-right">
            <v-btn color="primary" @click="createBroadcast" :loading="isCreating">
              {{ $vuetify.lang.t('$vuetify.gpt_broadcast.broadcast_message') }}
            </v-btn>
          </v-col>
        </v-row>
        </v-form>
      </v-card>

      <v-divider class="my-8"></v-divider>

      <!-- Broadcast History Section -->
      <div class="d-flex align-center justify-space-between mb-4 flex-wrap">
        <div class="text-h6">{{ $vuetify.lang.t('$vuetify.gpt_broadcast.history') }}</div>
      </div>
      
      <!-- Filters -->
      <v-row>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="filters.search"
            :label="$vuetify.lang.t('$vuetify.gpt_broadcast.search_messages')"
            outlined
            dense
            clearable
            prepend-inner-icon="mdi-magnify"
            hide-details
            @input="debouncedBroadcastFilterChange"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="filters.profile_id"
            :items="profileFilterOptions"
            item-text="name"
            item-value="id"
            :label="$vuetify.lang.t('$vuetify.gpt_broadcast.filter_profile')"
            outlined
            dense
            clearable
            hide-details
            @change="onBroadcastFilterChange"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="filters.status"
            :items="statusOptions"
            :label="$vuetify.lang.t('$vuetify.gpt_broadcast.filter_status')"
            outlined
            dense
            clearable
            hide-details
            @change="onBroadcastFilterChange"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="filters.delivery_mode"
            :items="deliveryModeFilterOptions"
            :label="$vuetify.lang.t('$vuetify.gpt_broadcast.filter_delivery_mode')"
            outlined
            dense
            clearable
            hide-details
            @change="onBroadcastFilterChange"
          ></v-select>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-data-table
        :headers="headers"
        :items="broadcastList"
        class="elevation-1 mt-4"
        :loading="isLoadingHistory"
        :server-items-length="totalBroadcasts"
        :options.sync="tableOptions"
        :footer-props="{ 'items-per-page-options': [5, 10, 25, 50, 100] }"
      >
        <template v-slot:[`item.profiles`]="{ item }">
          <div v-if="!item.profile_ids || item.profile_ids.length === 0">
            <v-chip small outlined color="primary">{{ $vuetify.lang.t('$vuetify.gpt_broadcast.all_profiles') }}</v-chip>
          </div>
          <div v-else>
            <v-chip
              v-for="pId in item?.profile_ids?.slice(0, 3)"
              :key="pId"
              small
              outlined
              color="primary"
              class="mr-1 mb-1"
            >
              {{ getProfileName(pId) }}
            </v-chip>
            <span v-if="item?.profile_ids?.length > 3">+{{ item.profile_ids.length - 3 }} more</span>
          </div>
        </template>
        <template v-slot:[`item.message`]="{ item }">
          <TruncateTooltip
            :text="item.message"
            :limit="50"
            :textMaxWidth="300"
            :tooltipMaxWidth="500"
            :tooltipOnlyWhenTruncated="true"
          />
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip small outlined :color="getStatusColor(item.status)">
            {{ item.status }}
          </v-chip>
        </template>
        <template v-slot:[`item.delivery_time`]="{ item }">
          {{ formatDateTime(item.delivery_time) }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small @click="viewBroadcastDetails(item)" v-bind="attrs" v-on="on">
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>{{ $vuetify.lang.t('$vuetify.gpt_broadcast.view_details_tooltip') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn 
                icon 
                small 
                @click="editBroadcastMessage(item)" 
                v-bind="attrs" 
                v-on="on"
                :disabled="item.status !== 'pending' || item.delivery_mode === 'immediate'"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>{{ $vuetify.lang.t('$vuetify.tooltip.edit') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small @click="confirmBroadcastDelete(item)" v-bind="attrs" v-on="on"
              :disabled="item.status !== 'pending' || item.delivery_mode === 'immediate'">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>{{ $vuetify.lang.t('$vuetify.tooltip.delete') }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small @click="reuseBroadcastMessage(item)" v-bind="attrs" v-on="on">
                <v-icon small>mdi-content-copy</v-icon>
              </v-btn>
            </template>
            <span>{{ $vuetify.lang.t('$vuetify.gpt_broadcast.reuse_tooltip') }}</span>
          </v-tooltip>
        </template>
        <template v-slot:no-data>
          <div class="text-center pa-8 grey--text">
            {{ $vuetify.lang.t('$vuetify.gpt_broadcast.no_messages') }}
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $vuetify.lang.t('$vuetify.gpt_broadcast.edit_broadcast') }}</v-card-title>
        <v-card-text>
          <v-form ref="editBroadcastForm">
          <v-row>
            <v-col cols="12">
               <div class="textarea-with-tooltip">
                 <v-textarea 
                   v-model="editingBroadcast.message" 
                   :label="$vuetify.lang.t('$vuetify.gpt_broadcast.message')" 
                   :rules="[requiredMessageRule]"
                   outlined 
                   rows="3"
                   hide-details="auto"
                 ></v-textarea>
                 <v-tooltip bottom max-width="350">
                   <template v-slot:activator="{ on, attrs }">
                     <span class="info-icon" v-bind="attrs" v-on="on">i</span>
                   </template>
                   <span>{{ $vuetify.lang.t('$vuetify.gpt_broadcast.message_tooltip') }}</span>
                 </v-tooltip>
               </div>
            </v-col>
            <v-col cols="12">
              <div class="profile-autocomplete-wrapper">
                <v-autocomplete
                  v-model="editingBroadcast.profile_ids"
                  class="profile-autocomplete-chips"
                  :items="gptProfilesLocal"
                  item-text="name"
                  item-value="id"
                  :label="$vuetify.lang.t('$vuetify.gpt_broadcast.select_profile')"
                  outlined
                  dense
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  clearable
                  :menu-props="{ closeOnClick: true, closeOnContentClick: false }"
                >
                  <template
                    v-slot:append-item
                    v-if="gptProfilesLocal.length < gpt_profiles_total_hits"
                  >
                    <v-list-item>
                      <v-list-item-content>
                        <div class="d-flex">
                          <v-btn
                            class="ma-0 pa-2"
                            @click="loadMoreProfiles()"
                            color="primary"
                            text
                            :loading="isLoadingProfiles"
                          >
                            {{ $vuetify.lang.t("$vuetify.common.load_more") }}
                          </v-btn>
                        </div>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-autocomplete>
                <v-tooltip bottom max-width="350">
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      class="info-icon profile-tooltip-icon"
                      v-bind="attrs"
                      v-on="on"
                      @mousedown.prevent.stop
                      @click.stop
                      >i</span>
                  </template>
                  <span>{{ $vuetify.lang.t('$vuetify.gpt_broadcast.profile_tooltip') }}</span>
                </v-tooltip>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="delivery-mode-wrapper">
                <v-select 
                  v-model="editingBroadcast.delivery_mode" 
                  :items="deliveryModeOptions" 
                  :label="$vuetify.lang.t('$vuetify.gpt_broadcast.delivery_mode')"
                  outlined 
                  dense
                ></v-select>
                <v-tooltip bottom max-width="350">
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      class="info-icon delivery-mode-tooltip-icon"
                      v-bind="attrs"
                      v-on="on"
                      @mousedown.prevent.stop
                      @click.stop
                    >i</span>
                  </template>
                  <span>{{ $vuetify.lang.t('$vuetify.gpt_broadcast.delivery_mode_tooltip') }}</span>
                </v-tooltip>
              </div>
            </v-col>
            <v-col cols="12" md="6" v-if="editingBroadcast.delivery_mode === 'scheduled'">
              <div style="position: relative;">
                <v-datetime-picker
                  v-model="editingBroadcast.delivery_time"
                  :text-field-props="{ 
                    outlined: true, 
                    dense: true, 
                    label: $vuetify.lang.t('$vuetify.gpt_broadcast.delivery_time'),
                    placeholder: $vuetify.lang.t('$vuetify.gpt_broadcast.delivery_time_placeholder'),
                    rules: [requiredDeliveryTimeRule]
                  }"
                  :date-picker-props="{ min: new Date().toISOString().split('T')[0] }"
                  :time-picker-props="{ format: '24hr' }"
                >
                </v-datetime-picker>
                <v-tooltip bottom max-width="350">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-on="on" v-bind="attrs" class="info-icon info-icon-scheduled">i</span>
                  </template>
                  <span>{{ $vuetify.lang.t('$vuetify.gpt_broadcast.delivery_time_tooltip') }}</span>
                </v-tooltip>
              </div>
            </v-col>
          </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn outlined color="error" @click="editDialog = false">{{ $vuetify.lang.t('$vuetify.common.cancel_btn') }}</v-btn>
          <v-btn color="primary" @click="updateBroadcast" :loading="isUpdating">{{ $vuetify.lang.t('$vuetify.project.update') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <DialogModal
      v-model="deleteDialog"
      :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
      :message="$vuetify.lang.t('$vuetify.gpt_broadcast.confirm_delete_alert_msg_txt')"
      :confirmButtonText="$vuetify.lang.t('$vuetify.common.confirm_btn')"
      :cancelButtonText="$vuetify.lang.t('$vuetify.common.cancel_btn')"
      :confirmBtnLoading="isDeleting"
      @onConfirmation="deleteBroadcastConfirmed"
      @onCancel="deleteDialog = false"
    />


    <!-- Details Modal -->
    <v-dialog v-model="detailsDialog" max-width="700px">
      <v-card>
        <v-card-title>
          {{ $vuetify.lang.t('$vuetify.gpt_broadcast.broadcast_details') }}
        </v-card-title>
        <v-card-text>
          <v-row v-if="selectedBroadcast">
            <v-col cols="12">
              <div class="text-subtitle-2 mb-1">{{ $vuetify.lang.t('$vuetify.gpt_broadcast.message') }}</div>
              <div class="pa-3 grey lighten-4 rounded" style="position: relative; border: 1px solid #e0e0e0;">
                <div class="text-subtitle-2 black--text mr-6">{{ selectedBroadcast.message }}</div>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn 
                      icon 
                      x-small 
                      @click="copyToClipboard(selectedBroadcast.message)" 
                      v-bind="attrs" 
                      v-on="on"
                      style="position: absolute; top: 8px; right: 8px;"
                    >
                      <v-icon x-small>mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $vuetify.lang.t('$vuetify.common.copy_msg_txt') }}</span>
                </v-tooltip>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-subtitle-2">{{ $vuetify.lang.t('$vuetify.gpt_broadcast.status') }}</div>
              <v-chip small outlined :color="getStatusColor(selectedBroadcast.status)">
                {{ selectedBroadcast.status }}
              </v-chip>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-subtitle-2">{{ $vuetify.lang.t('$vuetify.gpt_broadcast.delivery_mode') }}</div>
              <div class="text-body-1 black--text">{{ selectedBroadcast.delivery_mode }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-subtitle-2">{{ $vuetify.lang.t('$vuetify.gpt_broadcast.delivery_time') }}</div>
              <div class="text-body-1 black--text">{{ formatDateTime(selectedBroadcast.delivery_time) }}</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-subtitle-2">{{ $vuetify.lang.t('$vuetify.gpt_broadcast.created_at') }}</div>
              <div class="text-body-1 black--text">{{ formatDateTime(selectedBroadcast.created_at) }}</div>
            </v-col>
            <v-col cols="12" md="6" v-if="selectedBroadcast.delivered_at">
              <div class="text-subtitle-2">{{ $vuetify.lang.t('$vuetify.gpt_broadcast.delivered_at') }}</div>
              <div class="text-body-1 black--text">{{ formatDateTime(selectedBroadcast.delivered_at) }}</div>
            </v-col>
            <v-col cols="12">
              <div class="text-subtitle-2">{{ $vuetify.lang.t('$vuetify.gpt_broadcast.profiles') }}</div>
              <div v-if="selectedBroadcast.profile_ids && selectedBroadcast.profile_ids.length">
                <v-chip
                  v-for="pId in selectedBroadcast.profile_ids"
                  :key="pId"
                  x-small
                  outlined
                  color="primary"
                  class="mr-1 mb-1"
                >
                  {{ getProfileName(pId) }}
                </v-chip>
              </div>
              <div v-else>
                <v-chip x-small outlined color="primary">{{ $vuetify.lang.t('$vuetify.gpt_broadcast.all_profiles') }}</v-chip>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn outlined color="error" @click="detailsDialog = false">{{ $vuetify.lang.t('$vuetify.common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import debounce from "lodash/debounce";
import { 
  GET_BROADCASTS_MESSAGE,
  CREATE_BROADCAST_MESSAGE, 
  UPDATE_BROADCAST_MESSAGE, 
  DELETE_BROADCAST_MESSAGE,
  GET_GPT_PROFILES,
  ADD_ALERT 
} from "@/store/_actiontypes";
import DialogModal from "@/components/common/DialogModal";
import TruncateTooltip from "@/components/common/tooltip/TruncateTooltip";

export default {
  name: "GptBroadcast",
  components: { DialogModal, TruncateTooltip },
  data: () => ({
    newBroadcast: {
      profile_ids: [],
      message: "",
      delivery_mode: "immediate",
      delivery_time: null
    },
    editingBroadcast: {
      id: null,
      message: "",
      profile_ids: [],
      delivery_mode: "immediate",
      delivery_time: null
    },
    filters: {
      search: "",
      profile_id: null,
      status: null,
      delivery_mode: null
    },
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isLoadingHistory: false,
    hasPendingBroadcastFetch: false,
    isLoadingProfiles: false,
    skipWatcherFetch: false,
    editDialog: false,
    deleteDialog: false,
    detailsDialog: false,
    itemToDelete: null,
    selectedBroadcast: null,
    tableOptions: { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] },
    gptProfilesLocal: [],
    gpt_profiles_total_hits: 0,
    currentProfilePage: 0,
    profilePageSize: 100,
    headers: [
      { text: 'Message', value: 'message', sortable: false },
      { text: 'Profiles', value: 'profiles', sortable: false },
      { text: 'Delivery Time', value: 'delivery_time', sortable: true },
      { text: 'Status', value: 'status', sortable: false },
      { text: 'Actions', value: 'actions', sortable: false, align: 'right' }
    ]
  }),
  computed: {
    ...mapState("project", {
      gptProfiles: state => state.gptProfiles?.profiles || [],
      broadcasts: state => state.broadcasts || { items: [], total: 0 }
    }),
    profileOptions() {
      return this.gptProfilesLocal.map(p => ({ name: p.name, id: p.id }));
    },
    profileFilterOptions() {
      return this.gptProfilesLocal.map(p => ({ name: p.name, id: p.id }));
    },
    deliveryModeOptions() {
      return [
        { text: this.$vuetify.lang.t('$vuetify.gpt_broadcast.immediate'), value: 'immediate' },
        { text: this.$vuetify.lang.t('$vuetify.gpt_broadcast.scheduled'), value: 'scheduled' }
      ];
    },
    deliveryModeFilterOptions() {
      return [
        { text: this.$vuetify.lang.t('$vuetify.gpt_broadcast.immediate'), value: 'immediate' },
        { text: this.$vuetify.lang.t('$vuetify.gpt_broadcast.scheduled'), value: 'scheduled' }
      ];
    },
    statusOptions() {
      return ['pending', 'delivered'];
    },
    broadcastList() {
      return this.broadcasts.items || [];
    },
    totalBroadcasts() {
      return this.broadcasts.total || 0;
    }
  },
  watch: {
    tableOptions: {
      handler() {
        if (this.skipWatcherFetch) {
          this.skipWatcherFetch = false;
          return;
        }
        this.fetchBroadcasts();
      },
      deep: true
    }
  },
  created() {
    this.debouncedBroadcastFilterChange = debounce(this.onBroadcastFilterChange, 500);
    this.loadMoreProfiles();
    this.fetchBroadcasts();
  },
  mounted() {
    if (this.$socket && typeof this.$socket.on === "function") {
      this._unsubBroadcastRefresh = this.$socket.on(
        "broadcastListRefresh",
        this.onBroadcastSocketRefresh
      );
    }
  },
  beforeDestroy() {
    if (this.debouncedBroadcastFilterChange?.cancel) {
      this.debouncedBroadcastFilterChange.cancel();
    }
    if (typeof this._unsubBroadcastRefresh === "function") {
      this._unsubBroadcastRefresh();
    }
  },
  methods: {
    ...mapActions("project", [
      GET_BROADCASTS_MESSAGE,
      CREATE_BROADCAST_MESSAGE, 
      UPDATE_BROADCAST_MESSAGE, 
      DELETE_BROADCAST_MESSAGE,
      GET_GPT_PROFILES
    ]),
    getProfileName(id) {
       if (!id) return 'Unknown Profile';
       const p = this.gptProfilesLocal.find(p => p.id === id);
       return p ? p.name : 'Unknown Profile';
    },
    getStatusColor(status) {
      const statusLower = String(status).toLowerCase();
      if (statusLower === 'delivered') return 'success';
      if (statusLower === 'pending') return 'warning';
      return 'grey';
    },
    requiredMessageRule(value) {
      return value && String(value).trim().length > 0
        ? true
        : this.$vuetify.lang.t('$vuetify.gpt_broadcast.message_required');
    },
    requiredDeliveryTimeRule(value) {
      return value
        ? true
        : this.$vuetify.lang.t('$vuetify.gpt_broadcast.delivery_time_required');
    },
    formatDateTime(dateTime) {
      if (!dateTime || dateTime === 'immediate') {
        return this.$vuetify.lang.t('$vuetify.gpt_broadcast.immediate');
      }
      try {
        return new Date(dateTime).toLocaleString();
      } catch {
        return dateTime;
      }
    },
    onBroadcastFilterChange() {
      if (this.tableOptions.page !== 1) {
        this.tableOptions = {
          ...this.tableOptions,
          page: 1
        };
        return;
      }
      this.fetchBroadcasts();
    },
    onBroadcastSocketRefresh({ projectId }) {
      const pid = this.$route?.query?.pid;
      if (!pid) return;
      if (projectId != null && String(projectId) !== String(pid)) return;
      this.fetchBroadcasts();
    },
    async fetchBroadcasts() {
      if (this.isLoadingHistory) {
        this.hasPendingBroadcastFetch = true;
        return;
      }
      this.isLoadingHistory = true;
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions;
        
        // Build sort_by parameter for backend
        let sort_by = null;
        if (sortBy && sortBy.length > 0) {
          sort_by = sortBy.map((field, index) => ({
            [field]: sortDesc[index] ? 'desc' : 'asc'
          }));
        }
        
        await this[GET_BROADCASTS_MESSAGE]({
          project_id: this.$route?.query?.pid,
          profile_id: this.filters.profile_id,
          status: this.filters.status,
          delivery_mode: this.filters.delivery_mode,
          search: this.filters.search,
          sort_by,
          start: (page - 1) * itemsPerPage,
          size: itemsPerPage
        });
      } catch (err) {
        console.error(err);
        this.$store.dispatch(`alert/${ADD_ALERT}`, { 
          message: this.$vuetify.lang.t('$vuetify.gpt_broadcast.get_broadcast_error_msg_txt'), 
          color: "error" 
        }, { root: true });
      } finally {
        this.isLoadingHistory = false;
        if (this.hasPendingBroadcastFetch) {
          this.hasPendingBroadcastFetch = false;
          this.fetchBroadcasts();
        }
      }
    },
    async loadMoreProfiles() {
      try {
        if (this.isLoadingProfiles) return;
        if (!this.$route?.query?.pid) {
          this.isLoadingProfiles = false;
          return;
        }
        this.isLoadingProfiles = true;
        const apiRes = await this[GET_GPT_PROFILES]({
          project_id: this.$route?.query?.pid,
          start: this.currentProfilePage * this.profilePageSize,
          size: this.profilePageSize,
        });
        if (!apiRes || !apiRes.data) {
          this.isLoadingProfiles = false;
          return;
        }
        const newProfiles = apiRes?.data?.detail?.response?.profiles || [];
        if (this.currentProfilePage === 0) {
          this.gptProfilesLocal = newProfiles;
        } else {
          this.gptProfilesLocal = [...this.gptProfilesLocal, ...newProfiles];
        }
        this.gpt_profiles_total_hits = apiRes?.data?.detail?.response?.total_hits || 0;
        this.currentProfilePage += 1;
        this.isLoadingProfiles = false;
      } catch (e) {
        console.error("Error loading GPT profiles:", e);
        this.isLoadingProfiles = false;
      }
    },
    viewBroadcastDetails(item) {
      this.selectedBroadcast = item;
      this.detailsDialog = true;
    },
    validateBroadcastForm() {
      const isFormValid = this.$refs.createBroadcastForm?.validate
        ? this.$refs.createBroadcastForm.validate()
        : true;
      return isFormValid;
    },
    formatDeliveryTime(deliveryMode, deliveryTime) {
      if (deliveryMode === 'immediate') {
        return 'immediate';
      }
      
      if (!deliveryTime) {
        return 'immediate';
      }
      
      if (deliveryTime instanceof Date) {
        if (isNaN(deliveryTime.getTime())) {
          return 'immediate';
        }
        return deliveryTime.toISOString();
      }
      
      if (typeof deliveryTime === 'string' && deliveryTime !== 'immediate') {
        const date = new Date(deliveryTime);
        if (!isNaN(date.getTime())) {
          return date.toISOString();
        }
      }
      
      return deliveryTime;
    },
    handleApiError(error) {
      let errorMessage = this.$vuetify.lang.t('$vuetify.gpt_broadcast.update_error');
      
      if (error?.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        if (Array.isArray(detail)) {
          const messages = detail.map(err => err.msg || err.message).filter(Boolean);
          if (messages.length > 0) {
            errorMessage = messages.join(', ');
          }
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail.error) {
          errorMessage = detail.error;
        }
      }
      
      this.$store.dispatch(`alert/${ADD_ALERT}`, {
        message: errorMessage,
        color: "error"
      }, { root: true });
    },
    async createBroadcast() {
      if (!this.validateBroadcastForm()) return;
      
      this.isCreating = true;
      try {
        const deliveryTime = this.formatDeliveryTime(
          this.newBroadcast.delivery_mode,
          this.newBroadcast.delivery_time
        );
        
        await this[CREATE_BROADCAST_MESSAGE]({
          project_id: this.$route?.query?.pid,
          profile_ids: this.newBroadcast.profile_ids,
          message: this.newBroadcast.message,
          delivery_mode: this.newBroadcast.delivery_mode,
          delivery_time: deliveryTime
        });
        
        this.resetBroadcastForm();
        
        this.skipWatcherFetch = true;
        this.tableOptions = { ...this.tableOptions, page: 1 };
        await this.fetchBroadcasts();
        
        this.$store.dispatch(`alert/${ADD_ALERT}`, {
          message: this.$vuetify.lang.t('$vuetify.gpt_broadcast.create_success'),
          color: "success"
        }, { root: true });
      } catch (err) {
        console.error(err);
        this.handleApiError(err);
      } finally {
        this.isCreating = false;
      }
    },
    resetBroadcastForm() {
      this.newBroadcast = {
        profile_ids: [],
        message: "",
        delivery_mode: "immediate",
        delivery_time: null
      };
      this.$nextTick(() => {
        this.$refs.createBroadcastForm?.resetValidation?.();
      });
    },
    editBroadcastMessage(item) {
      const deliveryTime = item.delivery_time && item.delivery_time !== 'immediate' 
        ? new Date(item.delivery_time) 
        : null;
      
      this.editingBroadcast = {
        id: item.id,
        message: item.message,
        profile_ids: item.profile_ids ? [...item.profile_ids] : [],
        delivery_mode: item.delivery_mode || 'immediate',
        delivery_time: deliveryTime
      };
      this.editDialog = true;
      this.$nextTick(() => {
        this.$refs.editBroadcastForm?.resetValidation?.();
      });
    },
    async updateBroadcast() {
      const isFormValid = this.$refs.editBroadcastForm?.validate
        ? this.$refs.editBroadcastForm.validate()
        : true;
      if (!isFormValid) {
        return;
      }
      
      this.isUpdating = true;
      try {
        const deliveryTime = this.formatDeliveryTime(
          this.editingBroadcast.delivery_mode,
          this.editingBroadcast.delivery_time
        );
        
        await this[UPDATE_BROADCAST_MESSAGE]({
          project_id: this.$route?.query?.pid,
          broadcast_id: this.editingBroadcast.id,
          message: this.editingBroadcast.message,
          profile_ids: this.editingBroadcast.profile_ids,
          delivery_mode: this.editingBroadcast.delivery_mode,
          delivery_time: deliveryTime
        });
        
        await this.fetchBroadcasts();
        
        this.editDialog = false;
        
        this.$store.dispatch(`alert/${ADD_ALERT}`, {
          message: this.$vuetify.lang.t('$vuetify.gpt_broadcast.update_success'),
          color: "success"
        }, { root: true });
      } catch (err) {
        console.error(err);
        this.handleApiError(err);
      } finally {
        this.isUpdating = false;
      }
    },
    confirmBroadcastDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    async deleteBroadcastConfirmed() {
      if (!this.itemToDelete) return;
      this.isDeleting = true;
      try {
        await this[DELETE_BROADCAST_MESSAGE]({
          project_id: this.$route?.query?.pid,
          broadcast_id: this.itemToDelete.id
        });
        
        this.$store.dispatch(`alert/${ADD_ALERT}`, {
          message: this.$vuetify.lang.t('$vuetify.gpt_broadcast.delete_success'),
          color: "success"
        }, { root: true });
        
        await this.fetchBroadcasts();
      } catch (err) {
        console.error(err);
        this.handleApiError(err);
      } finally {
        this.isDeleting = false;
        this.deleteDialog = false;
        this.itemToDelete = null;
      }
    },
    reuseBroadcastMessage(item) {
      this.newBroadcast.message = item.message;
      this.newBroadcast.profile_ids = item.profile_ids ? [...item.profile_ids] : [];
      this.newBroadcast.delivery_mode = item.delivery_mode || 'immediate';
      this.newBroadcast.delivery_time = null;
      
      this.$store.dispatch(`alert/${ADD_ALERT}`, {
        message: this.$vuetify.lang.t('$vuetify.gpt_broadcast.message_reused_toast'),
        color: 'info'
      }, { root: true });
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    copyToClipboard(text) {
      if (!text) return;
      
      navigator.clipboard.writeText(text).then(() => {
        this.$store.dispatch(`alert/${ADD_ALERT}`, {
          message: this.$vuetify.lang.t('$vuetify.common.copied_msg_txt'),
          color: 'success'
        }, { root: true });
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    }
  }
}
</script>

<style scoped>
.field-row {
  margin-bottom: 16px;
}
.no-hover :deep(.v-input--selection-controls__ripple) {
  display: none;
}
.append-icons {
  display: flex;
  align-items: center;
  gap: 4px;
}
.info-icon {
  display: inline-block;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #333;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 8px;
  pointer-events: auto;
}
.textarea-with-tooltip {
  position: relative;
}
.textarea-with-tooltip .info-icon {
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 2;
}
.info-icon-scheduled {
  position: absolute;
  right: 12px;
  top: 10px;
  z-index: 1;
}
.profile-autocomplete-wrapper,
.delivery-mode-wrapper {
  position: relative;
}

.profile-tooltip-icon,
.delivery-mode-tooltip-icon {
  position: absolute;
  right: 8px;
  top: 12px;
  z-index: 2;
}

.profile-autocomplete-wrapper :deep(.v-input__icon--append),
.delivery-mode-wrapper :deep(.v-input__icon--append) {
  margin-right: 20px;
}
.v-application--is-ltr .v-textarea.v-text-field--enclosed .v-text-field__slot {
  padding-right: 30px;
}
.profile-autocomplete-chips :deep(.v-input__slot) {
  align-items: flex-start;
}
.profile-autocomplete-chips :deep(.v-select__selections) {
  flex-wrap: wrap;
  align-items: flex-start;
  row-gap: 4px;
}
.profile-autocomplete-chips :deep(.v-chip) {
  max-width: 100%;
  height: auto !important;
  min-height: 24px;
}
.profile-autocomplete-chips :deep(.v-chip__content) {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  max-width: none;
  line-height: 1.3;
  padding-top: 4px;
  padding-bottom: 4px;
}
.profile-autocomplete-wrapper :deep(.v-select__selections) {
  gap: 4px;
  margin: 8px 0 2px 0;
}
</style>
